/**
 * The PERSON's own orcaloops-vault RPC definitions (OL-63-links).
 *
 * A credential is a human's identity — their GitHub account, their Claude
 * subscription — held so an agent can act **as them**. That makes this group
 * actor-scoped in the strongest sense the repo has:
 *
 * **No payload here carries a `personId` or a `customerId`.** The subject is
 * always `CurrentActor.person.id`, resolved by `AuthMiddleware`, and minting a
 * link FOR SOMEBODY ELSE lives only in the `AdminMiddleware` group
 * (`rpc/admin/orcaloops-credentials.rpc.ts`), gated by `requireCustomerMember`.
 * An actor-scoped group that accepted a subject id would be one missing check
 * away from letting a member collect a colleague's identity.
 *
 * **Possession of a link is necessary but never sufficient.** Unlike
 * `onboarding/resolveLink` — whose comment reads "the magic-link token is the
 * credential" and which is deliberately middleware-free, because what it gates
 * is an intake — every method here runs under `AuthMiddleware` and the two
 * redemption methods additionally compare `CurrentActor.person.id` to the
 * link's `person_id`. A forwarded link is inert.
 *
 * `CollectionMode` has an `oauth` member, but wave 1 collects by FORM and an
 * `oauth` link is refused. The seam that wires the hosted round-trip is
 * specified in full at the top of `orcaloops-credentials.router.ts`, beside the
 * `mode` handling it changes.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Credential, CredentialKind } from "@fm/db/models/Credential";
import { CollectionMode } from "@fm/db/models/CredentialCollectionLink";
import { CredentialEvent } from "@fm/db/models/CredentialEvent";
import { Schema } from "effect";
import { AuthMiddleware } from "../auth.tags.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized } from "../errors.ts";

// ============================================================================
// Errors
// ============================================================================

/**
 * Why a collection link could not be used.
 *
 * `not_yours` is the reason this whole group exists: the token resolved, the
 * link is live, and the signed-in caller is simply not the person it was minted
 * for. It is returned with the SAME message as `invalid` so the surface is not
 * an oracle for "does this token exist".
 *
 * @since 1.0.0
 */
export const CredentialLinkErrorReason = Schema.Literal("invalid", "expired", "consumed", "not_yours");
export type CredentialLinkErrorReason = Schema.Schema.Type<typeof CredentialLinkErrorReason>;

/**
 * Wire error for every collection-link refusal. Mirrors `OnboardingLinkError`
 * (`rpc/errors.ts`), with `not_yours` added — the reason that has no analogue on
 * a surface where the token IS the credential.
 *
 * @since 1.0.0
 */
export class CredentialLinkError extends Schema.TaggedError<CredentialLinkError>()(
  "CredentialLinkError",
  {
    reason: CredentialLinkErrorReason,
    message: Schema.String,
  },
) {}

// ============================================================================
// Payloads and results
// ============================================================================

/**
 * How many audit rows to return. The handler clamps it to
 * `CREDENTIAL_HISTORY_MAX_LIMIT`, so a hostile client cannot ask for the whole
 * table.
 *
 * NOTE the absence of a `personId`: the subject is the actor, always.
 *
 * @since 1.0.0
 */
export const CredentialHistoryInput = Schema.Struct({
  limit: Schema.Number,
});
export type CredentialHistoryInput = Schema.Schema.Type<typeof CredentialHistoryInput>;

/**
 * The credential to withdraw consent from. Owner-scoped: the handler refuses
 * anything the caller does not own, with no admin short-circuit.
 *
 * @since 1.0.0
 */
export const CredentialRevokeInput = Schema.Struct({
  credentialId: Schema.String,
});
export type CredentialRevokeInput = Schema.Schema.Type<typeof CredentialRevokeInput>;

/**
 * What the person wants to hand over. No subject id — the link is minted for
 * the ACTOR, and the actor's customer is resolved from their membership.
 *
 * @since 1.0.0
 */
export const MintCollectionLinkInput = Schema.Struct({
  kind: CredentialKind,
});
export type MintCollectionLinkInput = Schema.Schema.Type<typeof MintCollectionLinkInput>;

/**
 * The mint acknowledgement. `url` carries the plaintext capability ONCE — it is
 * never persisted and can never be recovered afterwards.
 *
 * @since 1.0.0
 */
export const MintCollectionLinkResult = Schema.Struct({
  linkId: Schema.String,
  url: Schema.String,
  expiresAt: Schema.DateTimeUtc,
});
export type MintCollectionLinkResult = Schema.Schema.Type<typeof MintCollectionLinkResult>;

/**
 * The capability the collect page holds.
 *
 * @since 1.0.0
 */
export const CollectionTokenInput = Schema.Struct({
  token: Schema.String,
});
export type CollectionTokenInput = Schema.Schema.Type<typeof CollectionTokenInput>;

/**
 * What the collect page needs in order to render a form: which kind, which
 * fields, how long it has left.
 *
 * It deliberately never echoes the token, carries no secret material, and
 * reveals nothing about the person — a caller who is not the link's person gets
 * `CredentialLinkError` instead of this, so nothing here leaks either way.
 *
 * @since 1.0.0
 */
export const ResolveCollectionLinkResult = Schema.Struct({
  kind: CredentialKind,
  mode: CollectionMode,
  provider: Schema.NullOr(Schema.String),
  fieldNames: Schema.Array(Schema.String),
  expiresAt: Schema.DateTimeUtc,
});
export type ResolveCollectionLinkResult = Schema.Schema.Type<typeof ResolveCollectionLinkResult>;

/**
 * The person's secret, keyed by the kind's accepted field names. An unexpected
 * name is refused BEFORE any write.
 *
 * @since 1.0.0
 */
export const SubmitCollectionInput = Schema.Struct({
  token: Schema.String,
  label: Schema.String,
  values: Schema.Record({ key: Schema.String, value: Schema.String }),
});
export type SubmitCollectionInput = Schema.Schema.Type<typeof SubmitCollectionInput>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * The person's own orcaloops-vault RPC group. Every member carries
 * {@link AuthMiddleware}.
 *
 * The wire rows are `Credential.json` and `CredentialEvent.json` from
 * `@fm/db/models/*` — never redeclared. `Credential.json` has NO
 * `access_token` / `refresh_token` / `secret_fields` key at all, because OL-63
 * declared those columns `Model.Sensitive`; that omission is mechanical, so a
 * future handler edit cannot leak one.
 *
 * @since 1.0.0
 */
export class OrcaloopsCredentialRpcs extends RpcGroup.make(
  Rpc.make("credentials.list", {
    success: Schema.Array(Credential.json),
    error: Schema.Union(Unauthorized, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("credentials.history", {
    payload: CredentialHistoryInput,
    success: Schema.Array(CredentialEvent.json),
    error: Schema.Union(Unauthorized, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("credentials.revoke", {
    payload: CredentialRevokeInput,
    success: Credential.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("credentials.mintCollectionLink", {
    payload: MintCollectionLinkInput,
    success: MintCollectionLinkResult,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("credentials.resolveCollectionLink", {
    payload: CollectionTokenInput,
    success: ResolveCollectionLinkResult,
    error: Schema.Union(Unauthorized, CredentialLinkError, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("credentials.submitCollection", {
    payload: SubmitCollectionInput,
    success: Credential.json,
    error: Schema.Union(Unauthorized, CredentialLinkError, DatabaseError),
  }).middleware(AuthMiddleware),
) {}
